/**
 * NewsSectionCard / ImageSectionEditor をドラッグ&ドロップ可能にするラッパー。
 * 仕様書: docs/01_spec/spec_line_bot.md A-10.5（メール側 Phase 22b 同等）
 *
 * @dnd-kit/sortable の useSortable を利用。
 * カード左端にホバーで grip ハンドル表示。
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import type { LineNewsSection } from '@goenhub/shared';

import { ImageSectionEditor } from './ImageSectionEditor';
import { NewsSectionCard } from './NewsSectionCard';

interface Props {
  newsId: string;
  section: LineNewsSection;
  onChange: (next: LineNewsSection) => void;
  onDelete: () => void;
}

export function SortableNewsSection({ newsId, section, onChange, onDelete }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className="group">
      <button
        ref={setActivatorNodeRef}
        type="button"
        {...attributes}
        {...listeners}
        className="absolute -left-6 top-2 size-6 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--accent)] cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        title="ドラッグして並べ替え"
        aria-label="セクションを並べ替え"
      >
        <GripVertical className="size-4" />
      </button>
      {section.type === 'image' ? (
        <ImageSectionEditor
          newsId={newsId}
          section={section}
          onChange={onChange}
          onDelete={onDelete}
        />
      ) : (
        <NewsSectionCard
          section={section}
          onChange={onChange}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}
